import { App } from "@capacitor/app";
import { Capacitor } from "@capacitor/core";
import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { logger } from "@/logger";

type ResumeState = "idle" | "resuming" | "refetching" | "ready" | "error";

/**
 * App Lifecycle Provider
 *
 * Handles app state changes (background/foreground) and ensures critical data is fresh when app
 * resumes from background.
 *
 * On app resume, this provider:
 *
 * - Refetches user session and profile data
 * - Refetches organization data
 * - Refetches theme configuration
 * - Logs state changes for debugging
 * - Handles errors gracefully to prevent white screen crashes
 */
export function AppLifecycleProvider(props: React.PropsWithChildren) {
  const queryClient = useQueryClient();
  const [resumeState, setResumeState] = useState<ResumeState>("idle");
  const resumeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isMountedRef = useRef(true);
  const isResumingRef = useRef(false);

  useEffect(() => {
    // Only set up listeners on native platforms
    if (!Capacitor.isNativePlatform()) {
      return;
    }

    // Mark component as mounted
    isMountedRef.current = true;

    let appStateListener: Awaited<ReturnType<typeof App.addListener>> | null = null;
    let resumeListener: Awaited<ReturnType<typeof App.addListener>> | null = null;

    // Safe setState wrapper to prevent updates on unmounted component
    const safeSetResumeState = (state: ResumeState) => {
      if (isMountedRef.current) {
        setResumeState(state);
      }
    };

    /** Safely refetch queries with error handling, timeout protection, and retry logic */
    const safeRefetchQueries = async () => {
      const startTime = Date.now();
      safeSetResumeState("refetching");

      try {
        logger.info("[AppLifecycle] Starting query refetch with timeout protection");

        // Create timeout promise (10 seconds max for refetch)
        const timeoutPromise = new Promise((_, reject) => {
          resumeTimeoutRef.current = setTimeout(() => {
            reject(new Error("Query refetch timeout after 10s"));
          }, 10000);
        });

        // Refetch critical queries sequentially to avoid race conditions
        const refetchPromise = (async () => {
          // Refetch user profile first
          try {
            await queryClient.refetchQueries({ queryKey: ["me"], type: "active" });
            logger.info("[AppLifecycle] Successfully refetched user profile");
          } catch (error) {
            // Check if session expired (401/403 errors)
            const isAuthError =
              error &&
              typeof error === "object" &&
              "status" in error &&
              (error.status === 401 || error.status === 403);

            if (isAuthError) {
              logger.warn(
                "[AppLifecycle] Session expired during resume, user needs to re-authenticate",
                {
                  type: "session_expired_on_resume",
                  status: (error as { status: number }).status,
                },
              );
              // Session expired - query will handle redirect to login
            } else {
              logger.error("[AppLifecycle] Failed to refetch user profile", error);
            }
            // Continue anyway - app can still work with cached data or will redirect to login
          }

          // Then refetch theme
          try {
            await queryClient.refetchQueries({ queryKey: ["theme"], type: "active" });
            logger.info("[AppLifecycle] Successfully refetched theme");
          } catch (error) {
            logger.error("[AppLifecycle] Failed to refetch theme", error);
            // Continue anyway - app can still work with cached theme
          }
        })();

        // Race between refetch and timeout
        await Promise.race([refetchPromise, timeoutPromise]);

        const duration = Date.now() - startTime;
        logger.info(`[AppLifecycle] Query refetch completed in ${duration}ms`);
        safeSetResumeState("ready");

        // Clear timeout
        if (resumeTimeoutRef.current) {
          clearTimeout(resumeTimeoutRef.current);
          resumeTimeoutRef.current = null;
        }
      } catch (error) {
        const duration = Date.now() - startTime;
        logger.error(`[AppLifecycle] Query refetch failed after ${duration}ms`, error, {
          duration,
          type: "app_resume_error",
        });

        safeSetResumeState("error");

        // Clear timeout
        if (resumeTimeoutRef.current) {
          clearTimeout(resumeTimeoutRef.current);
          resumeTimeoutRef.current = null;
        }

        // Don't throw - let app continue with cached data
        // The app should still be usable even if refetch fails
      }
    };

    /** Handle app resume with coordinated operations */
    const handleAppResume = async () => {
      // Prevent multiple resume processes from running simultaneously
      if (isResumingRef.current) {
        logger.info("[AppLifecycle] Resume already in progress, skipping");
        return;
      }

      isResumingRef.current = true;
      const resumeStartTime = Date.now();
      safeSetResumeState("resuming");

      logger.info("[AppLifecycle] App became active, starting resume process");

      try {
        // iOS needs time to complete native UI transitions
        // Wait 300ms before starting any operations
        await new Promise((resolve) => setTimeout(resolve, 300));

        // Additional delay for WebSocket coordination
        // This ensures auth check completes before WebSocket tries to reconnect
        await new Promise((resolve) => setTimeout(resolve, 200));

        // Start refetch process
        await safeRefetchQueries();

        const totalDuration = Date.now() - resumeStartTime;
        logger.info(`[AppLifecycle] Resume process completed in ${totalDuration}ms`, {
          duration: totalDuration,
          type: "app_resume_completed",
        });
      } finally {
        // Always reset the guard, even if there's an error
        isResumingRef.current = false;
      }
    };

    const setupListeners = async () => {
      // Listen to app state changes (active/background)
      appStateListener = await App.addListener("appStateChange", ({ isActive }) => {
        logger.info(`[AppLifecycle] App state changed: ${isActive ? "active" : "background"}`);

        if (isActive) {
          void handleAppResume();
        } else {
          safeSetResumeState("idle");
        }
      });

      // Listen to resume events (app coming back from background)
      resumeListener = await App.addListener("resume", () => {
        logger.info("[AppLifecycle] App resumed from background");
        void handleAppResume();
      });
    };

    void setupListeners();

    // Cleanup
    return () => {
      // Mark component as unmounted to prevent state updates
      isMountedRef.current = false;

      void appStateListener?.remove();
      void resumeListener?.remove();

      if (resumeTimeoutRef.current) {
        clearTimeout(resumeTimeoutRef.current);
        resumeTimeoutRef.current = null;
      }

      // Reset resume guard
      isResumingRef.current = false;
    };
  }, [queryClient]);

  return <>{props.children}</>;
}
